<template>
  <div class="google-login-container w-full">
    <div v-if="showDivider" class="divider-container my-4">
      <v-divider></v-divider>
      <span class="divider-text px-4">or</span>
      <v-divider></v-divider>
    </div>

    <div class="google-login-button-container d-flex justify-center align-center w-full">
      <div :id="buttonId" class="w-full flex justify-center google-btn-wrapper"></div>
      <v-btn
        v-if="showGuestLogin"
        outlined
        color="grey darken-2"
        class="ml-4 text-none"
        height="40"
        @click="handleGuestLogin"
      >
        <v-icon left>mdi-account</v-icon>
        Sign in as Guest
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GoogleLogin',
  props: {
    showGuestLogin: {
      type: Boolean,
      default: false
    },
    showDivider: {
      type: Boolean,
      default: true
    },
    buttonText: {
      type: String,
      default: 'signin_with'
    },
    buttonId: {
      type: String,
      default: 'google-login-button'
    }
  },
  data() {
    return {
      isLoading: false,
      googleClientId: process.env.VUE_APP_GOOGLE_CLIENT_ID || 'your-google-client-id',
      popupWindow: null
    }
  },
  mounted() {
    window.addEventListener('message', this.handlePostMessage)

    this.loadGoogleScript()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handlePostMessage)

    if (this.popupWindow && !this.popupWindow.closed) {
      this.popupWindow.close()
    }
  },
  methods: {
    loadGoogleScript() {
      if (typeof window.google !== 'undefined') {
        this.initializeGoogleAuth()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.onload = () => {
        this.initializeGoogleAuth()
      }
      script.onerror = () => {
        console.error('Failed to load Google OAuth script')
      }
      document.head.appendChild(script)
    },

    initializeGoogleAuth() {
      if (typeof window.google === 'undefined') {
        console.error('Google OAuth library not loaded')
        return
      }

      try {
        window.google.accounts.id.initialize({
          client_id: this.googleClientId,
          callback: this.handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: 'signin'
        })

        // Render the Google Sign-In button
        this.$nextTick(() => {
          const buttonContainer = document.getElementById(this.buttonId)
          if (buttonContainer) {
            buttonContainer.innerHTML = ''
            const containerWidth = buttonContainer.offsetWidth || 340
            window.google.accounts.id.renderButton(buttonContainer, {
              theme: 'outline',
              size: 'large',
              width: Math.min(Math.max(containerWidth, 240), 400),
              text: this.buttonText,
              shape: 'rectangular',
              logo_alignment: 'center'
            })
          }
        })
      } catch (error) {
        console.error('Error initializing Google Auth:', error)
      }
    },
    async handleGoogleResponse(response) {
      // console.log('Google OAuth response received:', response)

      try {
        this.isLoading = true

        // Handle the credential response
        if (response.credential) {
          // Decode the JWT token to get user info
          const userInfo = this.decodeJwt(response.credential)

          // Emit success event with token and user info
          this.$emit('google-login-success', {
            token: response.credential,
            user: userInfo
          })
        } else {
          throw new Error('No credential received from Google')
        }
      } catch (error) {
        console.error('Error handling Google response:', error)
        this.$emit('google-login-error', error)
      } finally {
        this.isLoading = false
      }
    },

    decodeJwt(token) {
      try {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
        return JSON.parse(jsonPayload)
      } catch (error) {
        console.error('Error decoding JWT:', error)
        return null
      }
    },

    // Handle postMessage from popup
    handlePostMessage(event) {
      if (event.origin !== window.location.origin) {
        return
      }

      if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
        // Close popup
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close()
        }

        // Use the access token to get user info
        this.fetchUserInfo(event.data.token)
      } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
        if (this.popupWindow && !this.popupWindow.closed) {
          this.popupWindow.close()
        }

        this.isLoading = false
        this.$emit('google-login-error', new Error(event.data.error))
      }
    },

    async fetchUserInfo(accessToken) {
      try {
        this.isLoading = true

        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const userInfo = await response.json()

        this.$emit('google-login-success', {
          token: accessToken,
          user: userInfo
        })
      } catch (error) {
        console.error('Error fetching user info:', error)
        this.$emit('google-login-error', error)
      } finally {
        this.isLoading = false
      }
    },
    handleGuestLogin() {
      this.$emit('guest-login', {
        username: '3928819330',
        passcode: '123456',
        organization_code: 'DEMORG'
      })
    }
  }
}
</script>

<style scoped>
.google-login-container {
  width: 100%;
}

.divider-container {
  display: flex;
  align-items: center;
}

.divider-text {
  color: #757575;
  font-size: 0.875rem;
  white-space: nowrap;
}
</style>
